// angular
import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  Renderer2,
  viewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
// services
import { StorageService } from '@app/shared/services/storage.service';
import { CurrencyService } from '@app/core/shared/services/currency.service';
// interfaces
import { PriceType } from '@app/+products/shared/interfaces/product.interface';
// enums
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
import { I18n } from '@shared/enums/i18n.enum';

type COLOR = 'success' | 'error' | 'white' | 'default';

@Component({
  selector: 'pwj-range-input',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: './range-input.component.html',
  styleUrl: './range-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: RangeInputComponent,
      multi: true,
    },
  ],
})
export class RangeInputComponent
  implements OnInit, ControlValueAccessor, AfterViewChecked, AfterViewInit
{
  public value: number = 0;
  public disabled = false;
  public minVal: number = 0;
  public maxVal: number = 0;
  public minPercent: number = 0;
  public maxPercent: number = 0;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;

  label = input<string>('');
  placeholder = input<string>('');
  icon = input<string>('');
  type = input<string>('text');
  isDisabled = input<boolean>(false);
  isFullWidth = input<boolean>(false);
  color = input<COLOR>('default');
  minRange = input.required<number>();
  maxRange = input.required<number>();
  rangeStep = input<number>(100000);

  range = viewChild<ElementRef | undefined>('range');
  rangeBox = viewChild<ElementRef | undefined>('rangeBox');

  // dependencies
  private readonly _renderer = inject(Renderer2);
  private readonly _translate = inject(TranslateService);
  private readonly _currenciesService = inject(CurrencyService);
  private readonly _storageService = inject(StorageService);

  constructor() {
    // set current currency
    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;
    effect(() => {
      this.currentCurrency = this._currenciesService.currentCurrency();
    });
  }

  ngOnInit(): void {
    this.isDisabled() && this.setDisabledState(this.isDisabled());
    this.minVal = Number(this.minRange());
    this.maxVal = Number(this.maxRange());
  }

  ngAfterViewInit() {
    this.updateRangePosition();
  }

  public setMinValue(e: any) {
    const value = Math.min(Number(e.target?.value), this.maxVal - 1);

    if (value >= this.maxVal - 2) {
      this.minVal = this.minRange();
    } else {
      this.minVal = value;
    }
    this.updateRangePosition();
  }

  public setMaxValue(e: any) {
    const calculateMaxNumber = Math.max(
      Number(e.target?.value),
      this.minVal + 1
    );
    this.maxVal =
      calculateMaxNumber < this.maxRange()
        ? calculateMaxNumber
        : this.maxRange();
    this.updateRangePosition();
  }

  updateRangePosition() {
    const centerPositionPercentage =
      ((this.minVal + this.maxVal) / 2 / (this.maxRange() - this.minRange())) *
      100;
    const valuesElement = this.rangeBox()?.nativeElement.querySelector(
      '.range-input__values'
    );
    if (valuesElement) {
      const valuesElementWidthPercentage = 10;
      this._renderer.setStyle(
        valuesElement,
        'left',
        `calc(${centerPositionPercentage}% - ${valuesElementWidthPercentage}%)`
      );
    }

    // set form value
    this.propagateChange({
      lowest_price: this.minVal.toString(),
      highest_price: this.maxVal.toString(),
    });
  }

  // Convert to percentage
  public getPercent(value: number) {
    return Math.round(
      ((value - this.minRange()) / (this.maxRange() - this.minRange())) * 100
    );
  }

  propagateChange = (_: any) => {};

  writeValue(value: { lowest_price: number; highest_price: number }) {
    if (value?.lowest_price && typeof value?.lowest_price === 'string') {
      this.minVal = value?.lowest_price;
    }

    if (value?.highest_price && typeof value?.highest_price === 'string') {
      this.maxVal = value?.highest_price;
    }
  }

  ngAfterViewChecked() {
    this.minPercent = this.getPercent(this.minVal);
    this.maxPercent = this.getPercent(this.maxVal);
    if (this.range) {
      const isRtl = (I18n as any)[this._translate.currentLang].includes(
        I18n.fa || I18n.ar
      );

      // set range left position value
      this._renderer.setStyle(
        this.range()?.nativeElement,
        isRtl ? 'right' : 'left',
        `${this.minPercent}%`
      );

      // set range width value
      this._renderer.setStyle(
        this.range()?.nativeElement,
        'width',
        `${this.maxPercent - this.minPercent}%`
      );
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
